import { useCallback, useEffect, useRef, useState } from "react"

import { Emitter } from "@/lib/emitter"
import { env } from "@/lib/env"
import { isEmbedded } from "@/lib/is-embedded"
import {
  buildTurnstileError,
  TURNSTILE_ERROR_INVALID_SITE_KEY,
  TURNSTILE_ERROR_INVALID_SITE_KEY_CONFIGURATION,
  TURNSTILE_ERROR_SITE_KEY_DISABLED,
  TURNSTILE_ERROR_SITE_KEY_NOT_FOUND,
  turnstileErrorAction,
} from "@/lib/turnstile"
import { InvisibleTurnstile } from "@/components/invisible-turnstile"
import { VisibleTurnstileDialog } from "@/components/visible-turnstile-dialog"

const cfEmitter = new Emitter<void>()

// These errors invalidate or disable the invisible site key. The visible
// challenge has a separately configured key and can still recover. Domain
// authorization failures cannot recover by changing widget modes.
const INVISIBLE_SITE_KEY_FALLBACK_ERROR_CODES = new Set([
  TURNSTILE_ERROR_INVALID_SITE_KEY,
  TURNSTILE_ERROR_SITE_KEY_NOT_FOUND,
  TURNSTILE_ERROR_INVALID_SITE_KEY_CONFIGURATION,
  TURNSTILE_ERROR_SITE_KEY_DISABLED,
])

export type CfTokenValue = { cf_token: string; type: "invisible" | "visible" }
type TurnstileViewState = "invisible" | "visible" | "resolved"

/*
 * A page can include this component when it needs a Turnstile token before
 * continuing. Code outside the component calls `fetchAnonTurnstileToken()`;
 * that call waits here until this component gets a token or decides Turnstile
 * is disabled.
 *
 * The first attempt is invisible Turnstile. On success, the token is saved, the
 * wait ends, and nothing more is shown. If the invisible widget is not enabled,
 * the disabled token is saved and the caller decides whether that is allowed.
 * Cloudflare owns automatic retries; after the retry budget is exhausted, this
 * shows the visible challenge. Invalid invisible configuration skips that budget
 * because the visible challenge uses a separate key. Terminal failures reject
 * the wait from the phase that received them.
 *
 * Turnstile callbacks can arrive late. For example, the invisible widget can
 * report another error after the visible challenge already succeeded. The
 * component keeps track of the current screen and ignores results from an older
 * screen so a finished challenge cannot be replaced by a stale error.
 */
let tokenValue: CfTokenValue = {
  cf_token: "",
  type: "invisible",
}
let tokenPromiseSettled = false
let nextAttemptId = 0
let activeAttemptId = 0
let finishTokenWait: (() => void) | null = null
let failTokenWait: ((error: Error) => void) | null = null

export const DISABLED_TOKEN: CfTokenValue = {
  cf_token: "turnstile_disabled",
  type: "invisible",
}

const tokenPromise = new Promise<void>((resolve, reject) => {
  finishTokenWait = resolve
  failTokenWait = reject
})

function completeTurnstileToken(value: CfTokenValue) {
  if (tokenPromiseSettled) {
    return
  }

  tokenValue = value
  tokenPromiseSettled = true
  finishTokenWait?.()
}

function failTurnstileToken(error: Error) {
  if (tokenPromiseSettled) {
    return
  }

  tokenPromiseSettled = true
  failTokenWait?.(error)
}

export async function fetchAnonTurnstileToken() {
  await tokenPromise
  return tokenValue
}

export function CreateAnonTurnstile() {
  const attemptId = useRef(0)
  if (attemptId.current === 0) {
    attemptId.current = ++nextAttemptId
  }

  const retryRef = useRef(0)
  const [viewState, setViewState] = useState<TurnstileViewState>(
    tokenPromiseSettled ? "resolved" : "invisible"
  )
  const viewStateRef = useRef<TurnstileViewState>(viewState)

  const setAttemptElement = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      activeAttemptId = attemptId.current
    } else if (activeAttemptId === attemptId.current) {
      activeAttemptId = 0
    }
  }, [])

  function isActiveAttempt() {
    return activeAttemptId === attemptId.current
  }

  useEffect(() => {
    if (viewStateRef.current !== "invisible") {
      return
    }

    cfEmitter.emit()
  }, [])

  function setCurrentViewState(nextViewState: TurnstileViewState) {
    viewStateRef.current = nextViewState
    setViewState(nextViewState)
  }

  async function onVerify(cfToken: string) {
    if (!isActiveAttempt() || viewStateRef.current !== "invisible") {
      return
    }

    completeTurnstileToken({
      cf_token: cfToken,
      type: "invisible",
    })
    setCurrentViewState("resolved")
  }

  function onInvisibleTerminalFailure(error: Error) {
    if (!isActiveAttempt() || viewStateRef.current !== "invisible") {
      return
    }

    failTurnstileToken(error)
    setCurrentViewState("resolved")
  }

  function showVisibleChallenge() {
    if (!env("NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY")) {
      onInvisibleTerminalFailure(
        new Error("Turnstile site key is not configured")
      )
      return
    }

    if (isEmbedded()) {
      window.parent.postMessage("mindtrip.view-loaded", "*")
    }
    setCurrentViewState("visible")
  }

  function onInvisibleError(errorCode: string) {
    if (!isActiveAttempt() || viewStateRef.current !== "invisible") {
      return
    }

    if (errorCode.startsWith("[turnstile_disabled]")) {
      completeTurnstileToken(DISABLED_TOKEN)
      setCurrentViewState("resolved")
      return
    }

    if (INVISIBLE_SITE_KEY_FALLBACK_ERROR_CODES.has(errorCode)) {
      showVisibleChallenge()
      return
    }

    const action = turnstileErrorAction(errorCode)
    if (action === "terminal") {
      onInvisibleTerminalFailure(buildTurnstileError(errorCode))
      return
    }

    if (action === "reset") {
      return
    }

    if (retryRef.current < 1) {
      retryRef.current++
      return
    }

    showVisibleChallenge()
  }

  function onVisibleVerify(cfToken: string) {
    if (!isActiveAttempt() || viewStateRef.current !== "visible") {
      return
    }

    completeTurnstileToken({
      cf_token: cfToken,
      type: "visible",
    })
    setCurrentViewState("resolved")
  }

  function onVisibleTerminalFailure(error: Error) {
    if (!isActiveAttempt() || viewStateRef.current !== "visible") {
      return
    }

    failTurnstileToken(error)
    setCurrentViewState("resolved")
  }

  function onVisibleError(errorCode: string) {
    if (!isActiveAttempt() || viewStateRef.current !== "visible") {
      return
    }

    const action = turnstileErrorAction(errorCode)
    if (action === "terminal") {
      onVisibleTerminalFailure(buildTurnstileError(errorCode))
    }
  }

  if (viewState === "resolved") {
    return null
  }

  return (
    <div ref={setAttemptElement}>
      {viewState === "visible" ? (
        <VisibleTurnstileDialog
          open
          onVerify={onVisibleVerify}
          onError={onVisibleError}
          onUnsupported={() =>
            onVisibleTerminalFailure(new Error("Turnstile is not supported"))
          }
          onScriptLoadError={() =>
            onVisibleTerminalFailure(
              new Error("Turnstile script failed to load")
            )
          }
        />
      ) : (
        <InvisibleTurnstile
          id="create-anon-invisible-turnstile"
          refreshEmitter={cfEmitter}
          onVerify={onVerify}
          onError={onInvisibleError}
          onUnsupported={() =>
            onInvisibleTerminalFailure(new Error("Turnstile is not supported"))
          }
          onScriptLoadError={() =>
            onInvisibleTerminalFailure(
              new Error("Turnstile script failed to load")
            )
          }
        />
      )}
    </div>
  )
}
